import { apiSlice } from "@/app/apiSlice";
import { API_ENDPOINTS } from "@/constants/apiEndpoints";
import { blobDownloadQuery } from "@/features/financialShared";
import type {
	ChargesManagementPropertiesResponse,
	ChargesServicePeriodsResponse,
	ChargesServicePeriodUnitsResponse,
	ChargesUnitPathArgs,
	CreateChargesServicePeriodBody,
	ExportChargesServicePeriodUnitsArgs,
	GetChargesManagementPropertiesArgs,
	GetChargesServicePeriodsArgs,
	GetChargesServicePeriodUnitsArgs,
	UpdateChargesUnitInvoiceArgs,
	UploadChargesServicePeriodArgs,
} from "./chargesManagement.api.types";
import {
	buildChargesManagementListParams,
	buildChargesServicePeriodListParams,
	parseChargesManagementPropertiesResponse,
	parseChargesServicePeriodsResponse,
} from "./chargesManagement.mappers";
import type { ChargesUnitDetail } from "./chargesManagement.types";
import {
	buildChargesServicePeriodUnitsParams,
	parseChargesServicePeriodUnitsResponse,
	parseChargesUnitInvoice,
} from "./chargesManagement.unitMappers";

function unitsTag(propertyId: string, periodId: string) {
	return {
		type: "ChargesServicePeriodUnits" as const,
		id: `${propertyId}:${periodId}`,
	};
}

export const chargesManagementApi = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		getChargesManagementProperties: builder.query<
			ChargesManagementPropertiesResponse,
			GetChargesManagementPropertiesArgs | undefined
		>({
			query: (args) => ({
				url: API_ENDPOINTS.properties.chargeManagement,
				params: buildChargesManagementListParams(args ?? {}),
			}),
			transformResponse: (raw: unknown) =>
				parseChargesManagementPropertiesResponse(raw),
			providesTags: [{ type: "ChargesManagementProperties", id: "LIST" }],
		}),
		getChargesServicePeriods: builder.query<
			ChargesServicePeriodsResponse,
			GetChargesServicePeriodsArgs
		>({
			query: (args) => ({
				url: API_ENDPOINTS.properties.chargesServicePeriods(args.propertyId),
				params: buildChargesServicePeriodListParams(args),
			}),
			transformResponse: (raw: unknown, _meta, args) =>
				parseChargesServicePeriodsResponse(raw, args.propertyId),
			providesTags: (_result, _error, args) => [
				{ type: "ChargesServicePeriods", id: args.propertyId },
			],
		}),
		createChargesServicePeriod: builder.mutation<
			unknown,
			{ propertyId: string; body: CreateChargesServicePeriodBody }
		>({
			query: ({ propertyId, body }) => ({
				url: API_ENDPOINTS.properties.chargesServicePeriods(propertyId),
				method: "POST",
				body,
			}),
			invalidatesTags: (_result, error, arg) =>
				error
					? []
					: [
							{ type: "ChargesServicePeriods", id: arg.propertyId },
							{ type: "ChargesManagementProperties", id: "LIST" },
						],
		}),
		publishChargesServicePeriod: builder.mutation<
			unknown,
			{ propertyId: string; periodId: string }
		>({
			query: ({ propertyId, periodId }) => ({
				url: API_ENDPOINTS.properties.publishChargesServicePeriod(
					propertyId,
					periodId,
				),
				method: "POST",
			}),
			invalidatesTags: (_result, error, arg) =>
				error
					? []
					: [
							{ type: "ChargesServicePeriods", id: arg.propertyId },
							{ type: "ChargesManagementProperties", id: "LIST" },
						],
		}),
		downloadChargesSampleXls: builder.mutation<DownloadedFile, void>({
			query: () =>
				blobDownloadQuery(
					API_ENDPOINTS.properties.chargesServicePeriodSampleXls,
					"charges-service-period-sample.xls",
				),
		}),
		getChargesServicePeriodUnits: builder.query<
			ChargesServicePeriodUnitsResponse,
			GetChargesServicePeriodUnitsArgs
		>({
			query: (args) => ({
				url: API_ENDPOINTS.properties.chargesServicePeriodUnits(
					args.propertyId,
					args.periodId,
				),
				params: buildChargesServicePeriodUnitsParams(args),
			}),
			transformResponse: (raw: unknown, _meta, args) =>
				parseChargesServicePeriodUnitsResponse(raw, {
					propertyId: args.propertyId,
					periodId: args.periodId,
				}),
			providesTags: (_result, _error, args) => [
				unitsTag(args.propertyId, args.periodId),
			],
		}),
		exportChargesServicePeriodUnits: builder.mutation<
			DownloadedFile,
			ExportChargesServicePeriodUnitsArgs
		>({
			query: ({ propertyId, periodId, scope }) =>
				blobDownloadQuery(
					API_ENDPOINTS.properties.exportChargesServicePeriodUnits(
						propertyId,
						periodId,
					),
					scope === "PENDING"
						? "charges-units-pending.xls"
						: "charges-units-all.xls",
					{ scope },
				),
		}),
		uploadChargesServicePeriod: builder.mutation<
			unknown,
			UploadChargesServicePeriodArgs
		>({
			query: ({ propertyId, periodId, body }) => ({
				url: API_ENDPOINTS.properties.uploadChargesServicePeriod(
					propertyId,
					periodId,
				),
				method: "POST",
				body,
			}),
			invalidatesTags: (_result, error, arg) =>
				error
					? []
					: [
							unitsTag(arg.propertyId, arg.periodId),
							{ type: "ChargesServicePeriods", id: arg.propertyId },
							{ type: "ChargesManagementProperties", id: "LIST" },
							{ type: "ChargesUnitInvoice", id: "LIST" },
						],
		}),
		getChargesUnitInvoice: builder.query<
			ChargesUnitDetail,
			ChargesUnitPathArgs
		>({
			query: ({ propertyId, periodId, unitId }) => ({
				url: API_ENDPOINTS.properties.chargesServicePeriodUnit(
					propertyId,
					periodId,
					unitId,
				),
			}),
			transformResponse: (raw: unknown, _meta, args) =>
				parseChargesUnitInvoice(raw, args),
			providesTags: (_result, _error, args) => [
				{ type: "ChargesUnitInvoice", id: args.unitId },
				{ type: "ChargesUnitInvoice", id: "LIST" },
			],
		}),
		updateChargesUnitInvoice: builder.mutation<
			unknown,
			UpdateChargesUnitInvoiceArgs
		>({
			query: ({ propertyId, periodId, unitId, body }) => ({
				url: API_ENDPOINTS.properties.chargesServicePeriodUnit(
					propertyId,
					periodId,
					unitId,
				),
				method: "PATCH",
				body,
			}),
			invalidatesTags: (_result, error, arg) =>
				error
					? []
					: [
							{ type: "ChargesUnitInvoice", id: arg.unitId },
							unitsTag(arg.propertyId, arg.periodId),
						],
		}),
		exportChargesUnitCsv: builder.mutation<DownloadedFile, ChargesUnitPathArgs>(
			{
				query: ({ propertyId, periodId, unitId }) =>
					blobDownloadQuery(
						API_ENDPOINTS.properties.exportChargesServicePeriodUnit(
							propertyId,
							periodId,
							unitId,
						),
						"charges-unit-invoice.csv",
					),
			},
		),
		downloadChargesUnitInvoicePdf: builder.mutation<
			DownloadedFile,
			ChargesUnitPathArgs
		>({
			query: ({ propertyId, periodId, unitId }) =>
				blobDownloadQuery(
					API_ENDPOINTS.properties.chargesServicePeriodUnitInvoice(
						propertyId,
						periodId,
						unitId,
					),
					"charges-unit-invoice.pdf",
				),
		}),
	}),
	overrideExisting: false,
});

export const {
	useGetChargesManagementPropertiesQuery,
	useGetChargesServicePeriodsQuery,
	useCreateChargesServicePeriodMutation,
	usePublishChargesServicePeriodMutation,
	useDownloadChargesSampleXlsMutation,
	useGetChargesServicePeriodUnitsQuery,
	useExportChargesServicePeriodUnitsMutation,
	useUploadChargesServicePeriodMutation,
	useGetChargesUnitInvoiceQuery,
	useUpdateChargesUnitInvoiceMutation,
	useExportChargesUnitCsvMutation,
	useDownloadChargesUnitInvoicePdfMutation,
} = chargesManagementApi;
